import { PrinterOutlined } from "@ant-design/icons";
import { Button, Tooltip, Typography } from "antd";
import axios from "axios";
import moment from "moment";
import React, { useRef } from "react";
import { useQuery } from "react-query";
import { useReactToPrint } from "react-to-print";
import { useSelector } from "react-redux";
import { cloudFrontUrl, serverUrl } from "../../../../../nestserver";
import { selectSchoolId } from "../../../../../redux/slices/auth.slice";

const { Title } = Typography;

const PrintField = ({ label, tooltip }) => (
  <div className="print-field">
    <span className="print-field-label" title={tooltip}>
      {label}
    </span>
    <span className="print-field-box" />
  </div>
);

const PrintCheckboxGroup = ({ label, options }) => (
  <div className="print-checkbox-group">
    <span className="print-field-label">{label}</span>
    <div className="print-checkbox-row">
      {options.map((option) => (
        <span key={option} className="print-checkbox-option">
          <span className="print-checkbox-box" /> {option}
        </span>
      ))}
    </div>
  </div>
);

const DOCUMENT_CHECKLIST = [
  "Birth Certificate",
  "Student Aadhaar Card",
  "Parent/Guardian Aadhaar Card",
  "Passport-size Photographs",
  "Transfer Certificate (previous school)",
  "Report Card / Mark Sheet",
  "Address Proof",
  "Caste/Category Certificate (if applicable)",
];

const DownloadApplication = () => {
  const schoolId = useSelector(selectSchoolId);
  const printRef = useRef();

  const schoolQuery = useQuery(
    ["school", schoolId],
    () =>
      axios
        .get(`${serverUrl}/school/${schoolId}`, {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        })
        .then((res) => res.data),
    { enabled: !!schoolId },
  );
  const school = schoolQuery.data?.result?.data;
  const applicationFee = school?.application_fee || 0;
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `admission-form-blank-${moment().format("YYYY-MM-DD_HHmm")}`,
  });

  return (
    <div>
      <div
        className="p-2 text-right flex justify-end items-center"
        style={{ gap: "0.5rem" }}
      >
        <Tooltip title='Opens the print dialog — choose "Save as PDF" as the destination to download it instead'>
          <Button
            type="primary"
            icon={
              <PrinterOutlined
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: "0.5rem",
                }}
              />
            }
            onClick={handlePrint}
          >
            Print / Save as PDF
          </Button>
        </Tooltip>
      </div>

      <div ref={printRef} className="admission-print-form p-8">
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <td style={{ padding: 0 }}>
                <div>
                  <div className="tc-doc">
                    <div className="upper-part">
                      <div className="relative w-20 h-20 rounded-full border-8 border-white">
                        <img
                          className="w-full h-full rounded-full border"
                          src={
                            school?.logo
                              ? `${cloudFrontUrl}/common/${school.logo}`
                              : "https://ui-avatars.com/api/?name=" +
                                (school?.name || "School")
                          }
                          alt="School logo"
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <p className="text-base font-semibold pb-1 capitalize">
                          {school?.name}
                        </p>
                        <p className="text-sm capitalize">
                          {school &&
                            `${school.address || ""}, ${school.city || ""}, ${
                              school.state || ""
                            } ${school.pincode || ""}`}
                        </p>
                        {school?.refId && (
                          <p className="text-sm">
                            <span className="font-semibold">
                              Aff No./School Code:
                            </span>{" "}
                            {school.refId}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ padding: 0 }}>
                <div>
                  <h3 className="text-xl font-semibold text-center pt-4 pb-6">
                    Admission Form
                  </h3>
                  <div>
                    <Title level={2}>1. Student Details</Title>
                    <div className="admission-form">
                      <PrintField label="First Name" />
                      <PrintField label="Last Name" />
                      <PrintCheckboxGroup
                        label="Gender"
                        options={["Male", "Female", "Other"]}
                      />
                    </div>
                    <div className="admission-form">
                      <PrintField label="Date of Birth" />
                      <PrintField label="Birth Place" />
                      <PrintField label="Aadhaar" />
                    </div>
                    <div className="admission-form">
                      <PrintField label="Enroll Year" />
                      <PrintField label="Class" />
                      <PrintField label="Nationality" />
                    </div>
                    <div className="admission-form">
                      <PrintField label="Mother Tongue" />
                      <PrintField label="Religion" />
                      <PrintField label="Category" />
                    </div>
                    <div className="admission-form">
                      <PrintField label="Ethnicity" />
                      <PrintField label="Email" />
                      <PrintField label="Phone" />
                    </div>
                  </div>

                  <div>
                    <Title level={2}>2. Health Details</Title>
                    <div className="admission-form">
                      <PrintField label="Blood Group" />
                      <PrintCheckboxGroup
                        label="Disabled"
                        options={["Yes", "No"]}
                      />
                    </div>
                  </div>

                  <div>
                    <Title level={2}>3. Parents &amp; Contact</Title>
                    <Title level={4} className="admission-subheading">
                      Father's Details
                    </Title>
                    <div className="admission-form">
                      <PrintField label="Name" />
                      <PrintField label="Phone" />
                      <PrintField label="Email" />
                      <PrintField label="Aadhaar" />
                    </div>
                    <div className="admission-form">
                      <PrintField label="Education" />
                      <PrintField label="Occupation" />
                      <PrintField label="Annual Income" />
                    </div>

                    <Title level={4} className="admission-subheading">
                      Mother's Details
                    </Title>
                    <div className="admission-form">
                      <PrintField label="Name" />
                      <PrintField label="Phone" />
                      <PrintField label="Email" />
                      <PrintField label="Aadhaar" />
                    </div>
                    <div className="admission-form">
                      <PrintField label="Education" />
                      <PrintField label="Occupation" />
                      <PrintField label="Annual Income" />
                    </div>

                    <Title level={4} className="admission-subheading">
                      Address &amp; Emergency Contact
                    </Title>
                    <div className="admission-form">
                      <PrintField label="Address" />
                      <PrintField label="Emergency Contact" />
                    </div>
                  </div>

                  <div>
                    <Title level={2}>4. Guardian (if applicable)</Title>
                    <div className="admission-form">
                      <PrintField label="First Name" />
                      <PrintField label="Last Name" />
                      <PrintField label="Relation" />
                    </div>
                    <div className="admission-form">
                      <PrintField label="Occupation" />
                      <PrintField label="Education" />
                      <PrintField label="Annual Income" />
                    </div>
                    <div className="admission-form">
                      <PrintField label="Phone" />
                      <PrintField label="Email" />
                      <PrintField label="Aadhaar" />
                    </div>
                    <div className="admission-form">
                      <PrintField label="Address" />
                      <PrintField label="Emergency Contact" />
                    </div>
                  </div>

                  <div>
                    <Title level={2}>5. Existing Siblings</Title>
                    {[1, 2, 3].map((row) => (
                      <div className="admission-form" key={row}>
                        <PrintField label="Name" />
                        <PrintField label="Student ID" />
                      </div>
                    ))}
                  </div>

                  <div>
                    <Title level={2}>6. Previous School</Title>
                    <PrintCheckboxGroup
                      label="Was the student previously enrolled at another school?"
                      options={["Yes", "No"]}
                    />
                    <div className="admission-form">
                      <PrintField label="School Name" />
                      <PrintField label="Class Completed" />
                      <PrintField label="Overall Grade" />
                    </div>
                    <div className="admission-form">
                      <PrintField label="Year Completed" />
                      <PrintField label="Reference Phone/Email" />
                    </div>
                    <div className="admission-form">
                      <PrintField
                        label="PEN"
                        tooltip="Permanent Education Number"
                      />
                      <PrintField label="UDISE Code" />
                      <PrintField label="APAAR ID" />
                    </div>
                    <div className="print-field">
                      <span className="print-field-label">
                        Reason For Change
                      </span>
                      <span
                        className="print-field-box"
                        style={{ height: "3rem" }}
                      />
                    </div>
                  </div>

                  <div>
                    <Title level={2}>
                      7. Transportation &amp; Accommodation
                    </Title>
                    <PrintCheckboxGroup
                      label="Transportation required?"
                      options={["Yes", "No"]}
                    />
                    <div className="admission-form">
                      <PrintField label="Route (office use)" />
                    </div>
                    <PrintCheckboxGroup
                      label="Accommodation required?"
                      options={["Yes", "No"]}
                    />
                  </div>

                  <div>
                    <Title level={2}>8. Referral</Title>
                    <PrintCheckboxGroup
                      label="How did you hear about us?"
                      options={[
                        "Advertisement",
                        "Social Networking Community",
                        "Newspaper",
                        "Friend",
                      ]}
                    />
                    <div className="admission-form">
                      <PrintField label="Referring Friend's Name" />
                      <PrintField label="Referring Friend's Email" />
                      <PrintField label="Referring Friend's Phone" />
                    </div>
                  </div>

                  <div>
                    <Title level={2}>9. Documents to Submit</Title>
                    <p className="text-sm">
                      Please tick and enclose the following documents with this
                      application:
                    </p>
                    <div className="print-doc-checklist">
                      {DOCUMENT_CHECKLIST.map((doc) => (
                        <span key={doc} className="print-checkbox-option">
                          <span className="print-checkbox-box" /> {doc}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="print-declaration">
                    <Title level={2}>10. Declaration</Title>
                    <p className="text-sm">
                      I hereby declare that the information provided above is
                      true and correct to the best of my knowledge and belief.
                    </p>
                    <div className="print-signature-row">
                      <span className="print-signature-line">
                        Parent/Guardian Signature
                      </span>
                      <span className="print-signature-line">Date</span>
                    </div>
                  </div>

                  <div className="print-office-use">
                    <Title level={4} className="admission-subheading">
                      11. For Office Use Only
                    </Title>
                    <div className="admission-form">
                      <PrintField label="Application Number" />
                      <PrintField label="Admission Number" />
                    </div>
                    <div className="admission-form">
                      <PrintField label="Received By" />
                      <PrintField label="Date Received" />
                    </div>
                    <PrintCheckboxGroup
                      label="Status"
                      options={["Pending", "Approved", "Rejected"]}
                    />
                    {applicationFee > 0 && (
                      <>
                        <div className="admission-form">
                          <span className="print-field-label">
                            Application Fee: ₹{applicationFee}
                          </span>
                        </div>
                        <PrintCheckboxGroup
                          label="Fee Payment Received?"
                          options={["Yes", "No"]}
                        />
                        <div className="admission-form">
                          <PrintField label="Amount Received" />
                          <PrintField label="Receipt No." />
                          <PrintField label="Date" />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DownloadApplication;
